// Vorbis comments and the FLAC PICTURE structure. Both FLAC and Ogg (Vorbis,
// Opus, FLAC-in-Ogg) use them, so they live here.
import { ascii, field, u32be, u32le, utf8Str, fmtBytes } from './bits';
import type { Explanation, Field, Picture, Segment, Tag } from './types';

const TAG_NAMES: Record<string, string> = {
  TITLE: 'Title', ARTIST: 'Artist', ALBUM: 'Album', ALBUMARTIST: 'Album artist', 'ALBUM ARTIST': 'Album artist',
  DATE: 'Year', YEAR: 'Year', TRACKNUMBER: 'Track', TRACKTOTAL: 'Total tracks', DISCNUMBER: 'Disc', GENRE: 'Genre',
  COMMENT: 'Comment', DESCRIPTION: 'Comment', COMPOSER: 'Composer', ENCODER: 'Encoder', ENCODED_BY: 'Encoded by',
  COPYRIGHT: 'Copyright', ISRC: 'ISRC', LYRICS: 'Lyrics', PERFORMER: 'Performer', LICENSE: 'License',
  REPLAYGAIN_TRACK_GAIN: 'ReplayGain (track)', REPLAYGAIN_ALBUM_GAIN: 'ReplayGain (album)',
};
export const normalizeVorbisKey = (k: string) => TAG_NAMES[k.toUpperCase()] ?? k;

export const PICTURE_TYPES = [
  'Other', 'File icon', 'Other file icon', 'Front cover', 'Back cover', 'Leaflet page', 'Media', 'Lead artist',
  'Artist', 'Conductor', 'Band', 'Composer', 'Lyricist', 'Recording location', 'During recording',
  'During performance', 'Movie screen capture', 'A bright coloured fish', 'Illustration', 'Band logotype',
  'Publisher logotype',
];

export interface VorbisCommentResult {
  segments: Segment[]; // vendor string + one segment per comment
  fields: Field[];
  tags: Tag[];
  vendor: string;
  pictures: Picture[];
  /** Byte offset just after the comment list. */
  end: number;
}

/**
 * Parses a Vorbis comment list (always little-endian lengths) that starts at
 * `start`. Handles METADATA_BLOCK_PICTURE comments (base64 FLAC picture).
 */
export function parseVorbisComment(b: Uint8Array, start: number, limit: number): VorbisCommentResult {
  const segments: Segment[] = [];
  const tags: Tag[] = [];
  const pictures: Picture[] = [];
  let p = start;
  const vendorLen = u32le(b, p);
  const vendor = utf8Str(b, p + 4, vendorLen);
  segments.push({
    kind: 'metadata', type: 'vorbis.vendor', name: 'Vendor string', start: p, end: p + 4 + vendorLen, summary: vendor,
    fields: [field('Length', vendorLen, p, 4, 'Length of the vendor string in bytes (little-endian).'),
      field('Vendor', vendor, p + 4, vendorLen, 'Names the library that wrote the file.')],
  });
  p += 4 + vendorLen;
  const count = u32le(b, p);
  const fields: Field[] = [
    field('Vendor', vendor, start + 4, vendorLen, 'Names the library that wrote the file.'),
    field('Comment count', count, p, 4, 'How many NAME=value comments follow.'),
  ];
  p += 4;
  for (let i = 0; i < count && p + 4 <= limit; i++) {
    const len = u32le(b, p);
    if (p + 4 + len > limit) break;
    const text = utf8Str(b, p + 4, Math.min(len, 4096));
    const eq = text.indexOf('=');
    const key = eq >= 0 ? text.slice(0, eq) : text;
    const value = eq >= 0 ? text.slice(eq + 1) : '';
    const seg: Segment = {
      kind: 'metadata', type: 'vorbis.comment', name: key, start: p, end: p + 4 + len,
      summary: value.length > 80 ? value.slice(0, 80) + '…' : value,
      fields: [field('Length', len, p, 4, 'Length of this comment in bytes.'),
        field(key, len > 4096 ? value + '…' : value, p + 4, len, 'A NAME=value pair in UTF-8. Names are not case-sensitive.')],
    };
    if (key.toUpperCase() === 'METADATA_BLOCK_PICTURE') {
      seg.type = 'vorbis.comment.picture';
      seg.summary = `Base64-encoded picture, ${fmtBytes(len)}`;
      seg.kind = 'image';
      // The picture bytes are base64 text here, so we can't point at raw image bytes.
    } else {
      tags.push({ key: normalizeVorbisKey(key), value, rawKey: key });
    }
    segments.push(seg);
    p += 4 + len;
  }
  return { segments, fields, tags, vendor, pictures, end: p };
}

/** Parses the FLAC PICTURE structure (big-endian), used in FLAC blocks and METADATA_BLOCK_PICTURE. */
export function parseFlacPicture(b: Uint8Array, start: number): { fields: Field[]; picture: Picture; children: Segment[] } {
  let p = start;
  const type = u32be(b, p);
  const fields: Field[] = [field('Picture type', `${PICTURE_TYPES[type] ?? 'Unknown'} (${type})`, p, 4, 'What the picture shows. 3 means front cover.', type)];
  p += 4;
  const mimeLen = u32be(b, p);
  const mime = ascii(b, p + 4, mimeLen);
  fields.push(field('MIME type', mime, p, 4 + mimeLen, 'The image format, like image/jpeg or image/png.'));
  p += 4 + mimeLen;
  const descLen = u32be(b, p);
  const desc = utf8Str(b, p + 4, descLen);
  fields.push(field('Description', desc || '(empty)', p, 4 + descLen, 'Free text about the picture.'));
  p += 4 + descLen;
  const w = u32be(b, p), h = u32be(b, p + 4), depth = u32be(b, p + 8), colors = u32be(b, p + 12);
  fields.push(field('Width', `${w} px`, p, 4, undefined, w));
  fields.push(field('Height', `${h} px`, p + 4, 4, undefined, h));
  fields.push(field('Color depth', `${depth} bits per pixel`, p + 8, 4, undefined, depth));
  fields.push(field('Indexed colors', colors, p + 12, 4, 'Palette size for indexed images such as GIF; 0 otherwise.'));
  p += 16;
  const dataLen = u32be(b, p);
  fields.push(field('Data length', dataLen, p, 4, 'Size of the image data that follows.'));
  p += 4;
  const picture: Picture = { mime, start: p, end: p + dataLen, description: desc, pictureType: PICTURE_TYPES[type] };
  const children: Segment[] = [
    { kind: 'metadata', type: 'flac.picture.header', name: 'Picture header', start, end: p, fields },
    {
      kind: 'image', type: 'image.data', name: `${mime || 'Image'} data`, start: p, end: p + dataLen,
      summary: fmtBytes(dataLen), image: { mime, start: p, end: p + dataLen },
    },
  ];
  return { fields, picture, children };
}

export const vorbisCommentExplanations: Record<string, Explanation> = {
  'vorbis.vendor': {
    title: 'Vendor string',
    body: 'The first item in a Vorbis comment block. It names the software library that wrote the file, such as `libFLAC` or `Lavf` (ffmpeg).',
  },
  'vorbis.comment': {
    title: 'Vorbis comment',
    body: 'One tag, stored as plain UTF-8 text in the form `NAME=value`. A 4-byte length comes first. Any name is allowed, and a name can repeat (for example, two ARTIST tags).',
  },
  'vorbis.comment.picture': {
    title: 'Embedded picture (as text)',
    body: 'Ogg files have no picture block, so cover art goes into a comment named `METADATA_BLOCK_PICTURE`. The image is base64 text, which makes it about 33% larger than the raw image.',
  },
  'image.data': {
    title: 'Image data',
    body: 'The raw bytes of an embedded picture, usually a JPEG or PNG. The audio player ignores these bytes. Large cover art can take a surprising share of a small file.',
  },
  'flac.picture.header': {
    title: 'Picture header',
    body: 'Describes the image that follows: its role (front cover, artist, …), format, size in pixels, and length in bytes.',
  },
};
